import React from "react";
import { addToast } from "@heroui/react";
import { t } from "@lingui/core/macro";
import { isHubspotBlockedByContentBlocker } from "@/utils/contentBlocker";
import { ensureHubspotLoader } from "@/utils/hubspotLoader";
import { isNativePlatform } from "@/utils/native";
import { sentryLog } from "@/utils/sentry-utils";
import { showSupportChatBlockedToast } from "@/utils/supportChatBlockedToast";
import { showSupportReplyToast } from "@/utils/supportReplyToast";
import {
  isHubspotNewMessageNotification,
  shouldAnnounceSupportReply,
} from "./supportReplyAnnouncement";

type UserInfo = {
  email: string;
  name?: string;
  lastName?: string;
  phone?: string;
};

const CHAT_OPEN_CLASS = "hubspot-chat-open";
// Set on <html> by HubSpot's embed while the widget is expanded.
const HUBSPOT_WIDGET_OPEN_CLASS = "hs-messages-widget-open";
const HUBSPOT_IFRAME_ID = "hubspot-conversations-iframe";

function isWidgetOpen() {
  return document.documentElement.classList.contains(HUBSPOT_WIDGET_OPEN_CLASS);
}

function setChatOpenClass(isOpen: boolean) {
  if (!isNativePlatform()) return;
  document.documentElement.classList.toggle(CHAT_OPEN_CLASS, isOpen);
}

export const useHubspotChat = (userInfo?: UserInfo) => {
  const [activeConversation, setActiveConversation] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const unreadCountRef = React.useRef(0);
  const lastReplyToastAtRef = React.useRef<number | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const openHandlerRef = React.useRef<() => void>(() => {});

  // A content blocker filters HubSpot out silently — no error surfaces, the chat
  // just never opens — so probe the embed URLs before blaming anything else.
  const detectContentBlocker = React.useCallback(async (reason: string) => {
    const isBlocked = await isHubspotBlockedByContentBlocker();

    if (isBlocked) {
      sentryLog.info("Support chat blocked by a content blocker", { reason });
      showSupportChatBlockedToast();
    }

    return isBlocked;
  }, []);

  const reportChatUnavailable = React.useCallback(
    async (reason: string) => {
      if (await detectContentBlocker(reason)) return;

      sentryLog.error("Support chat could not be opened", { reason });
      addToast({
        title: t`Napaka pri nalaganju klepeta`,
        description: t`Klepet ni bil naložen. Poskusite znova ali kontaktirajte podporo.`,
        color: "danger",
      });
    },
    [detectContentBlocker],
  );

  const identifyVisitor = React.useCallback(() => {
    if (!userInfo?.email) return;

    try {
      const _hsq = (window as any)._hsq || [];
      (window as any)._hsq = _hsq;

      _hsq.push([
        "identify",
        {
          email: userInfo.email,
          ...(userInfo.name && { firstname: userInfo.name }),
          ...(userInfo.lastName && { lastname: userInfo.lastName }),
        },
      ]);

      _hsq.push(["trackPageView"]);
    } catch (err) {
      console.error("Error identifying visitor in HubSpot:", err);
    }
  }, [userInfo]);

  const handlersBoundRef = React.useRef(false);
  const unbindConversationHandlersRef = React.useRef<(() => void) | null>(null);

  // Subscribing requires `window.HubSpotConversations`, which only exists after
  // the embed loads. That happens at mount for a marketing-consented user, but
  // on demand (support chat) for everyone else — so this must be callable from
  // both paths, and idempotent so the two never double-subscribe.
  const bindConversationHandlers = React.useCallback(() => {
    if (!window.HubSpotConversations || handlersBoundRef.current) return;
    handlersBoundRef.current = true;

    const onConversationStarted = (payload: {
      conversation?: { conversationId?: string };
    }) => {
      try {
        const conversationId = payload?.conversation?.conversationId;
        setActiveConversation(!!conversationId);
      } catch (err) {
        console.error("Error handling conversation started:", err);
      }
    };

    const onConversationClosed = () => {
      try {
        setActiveConversation(false);
      } catch (err) {
        console.error("Error handling conversation closed:", err);
      }
    };

    // The widget stays loaded after the user closes it (the launcher is hidden
    // in CSS instead), so replies keep arriving and surface here.
    const announceReply = () => {
      const now = Date.now();
      if (
        !shouldAnnounceSupportReply({
          isWidgetOpen: isWidgetOpen(),
          lastAnnouncedAt: lastReplyToastAtRef.current,
          now,
        })
      ) {
        return;
      }
      lastReplyToastAtRef.current = now;
      showSupportReplyToast(() => openHandlerRef.current());
    };

    const onIframeMessage = (event: MessageEvent) => {
      const iframe = document.getElementById(HUBSPOT_IFRAME_ID);
      if (!(iframe instanceof HTMLIFrameElement)) return;
      if (event.source !== iframe.contentWindow) return;
      if (isHubspotNewMessageNotification(event.data)) announceReply();
    };

    // Only fires when a thread goes from no unread to some; kept as a fallback
    // in case HubSpot stops posting the per-message notification.
    const onUnreadCountChanged = (payload: { unreadCount?: number }) => {
      const count = payload?.unreadCount ?? 0;
      if (count > unreadCountRef.current) announceReply();
      unreadCountRef.current = count;
      setUnreadCount(count);
    };

    const onWidgetClosed = () => {
      setChatOpenClass(false);
    };

    try {
      window.HubSpotConversations.on(
        "conversationStarted",
        onConversationStarted,
      );
    } catch (err) {
      console.error("Error subscribing to conversationStarted:", err);
    }

    try {
      window.HubSpotConversations.on(
        "conversationClosed",
        onConversationClosed,
      );
    } catch (err) {
      console.error("Error subscribing to conversationClosed:", err);
    }

    try {
      window.HubSpotConversations.on(
        "unreadConversationCountChanged",
        onUnreadCountChanged,
      );
    } catch (err) {
      console.error(
        "Error subscribing to unreadConversationCountChanged:",
        err,
      );
    }

    window.addEventListener("message", onIframeMessage);

    try {
      window.HubSpotConversations.on("widgetClosed", onWidgetClosed);
    } catch (err) {
      console.error("Error subscribing to widgetClosed:", err);
    }

    unbindConversationHandlersRef.current = () => {
      window.removeEventListener("message", onIframeMessage);

      try {
        window.HubSpotConversations?.off(
          "conversationStarted",
          onConversationStarted,
        );
      } catch (err) {
        console.error("Error unsubscribing from conversationStarted:", err);
      }

      try {
        window.HubSpotConversations?.off(
          "conversationClosed",
          onConversationClosed,
        );
      } catch (err) {
        console.error("Error unsubscribing from conversationClosed:", err);
      }

      try {
        window.HubSpotConversations?.off(
          "unreadConversationCountChanged",
          onUnreadCountChanged,
        );
      } catch (err) {
        console.error(
          "Error unsubscribing from unreadConversationCountChanged:",
          err,
        );
      }

      try {
        window.HubSpotConversations?.off("widgetClosed", onWidgetClosed);
      } catch (err) {
        console.error("Error unsubscribing from widgetClosed:", err);
      }

      handlersBoundRef.current = false;
      unbindConversationHandlersRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    bindConversationHandlers();
    return () => unbindConversationHandlersRef.current?.();
  }, [bindConversationHandlers]);

  const openHandler = React.useCallback(() => {
    if (!window.HubSpotConversations) {
      // The HubSpot loader is gated behind marketing consent; opening support
      // chat is a user-initiated, functional load, so inject it on demand and
      // open once Conversations is ready.
      setIsLoading(true);
      ensureHubspotLoader();

      let settled = false;
      window.hsConversationsOnReady = window.hsConversationsOnReady || [];
      window.hsConversationsOnReady.push(() => {
        settled = true;
        // The mount effect couldn't subscribe (embed wasn't present then); now
        // that Conversations is ready, bind before opening.
        bindConversationHandlers();
        openHandlerRef.current();
      });

      window.setTimeout(() => {
        if (settled) return;
        setIsLoading(false);
        void reportChatUnavailable("embed script never initialised");
      }, 8000);
      return;
    }

    identifyVisitor();

    try {
      const status = window.HubSpotConversations.widget.status();

      if (status.loaded) {
        window.HubSpotConversations.widget.open();
        setChatOpenClass(true);
        // On the on-demand path the first invocation set loading; re-enable the
        // button now that the widget is open.
        setIsLoading(false);
      } else {
        // Widget not loaded yet, load it and open once loaded
        setIsLoading(true);
        window.HubSpotConversations.widget.load();

        // Poll until widget is loaded, then open
        let retryCount = 0;
        const maxRetries = 50; // 5 seconds max (50 * 100ms)

        const checkAndOpen = () => {
          retryCount++;
          try {
            const currentStatus = window.HubSpotConversations.widget.status();

            if (currentStatus.loaded) {
              window.HubSpotConversations.widget.open();
              setChatOpenClass(true);
              setIsLoading(false);
            } else if (
              !currentStatus.pending &&
              !currentStatus.loaded &&
              retryCount >= 5
            ) {
              // Widget is not pending and not loaded - HubSpot likely hid it intentionally
              // (e.g., due to targeting rules or no welcome message), but a blocked
              // conversations embed looks identical from here, so probe quietly.
              setIsLoading(false);
              console.info(
                "HubSpot widget was hidden (likely due to targeting rules or no welcome message)",
              );
              void detectContentBlocker("widget neither pending nor loaded");
            } else if (retryCount >= maxRetries) {
              // Only reachable while the widget is still pending: the branch
              // above claims every not-pending-and-not-loaded status from the
              // fifth retry onward, so exhausting the retries means pending.
              setIsLoading(false);
              void reportChatUnavailable("widget still pending after retries");
            } else {
              setTimeout(checkAndOpen, 100);
            }
          } catch (err) {
            console.error("Error checking widget status:", err);
            setIsLoading(false);
            // Only show error toast if we've tried multiple times
            if (retryCount >= 10) {
              void reportChatUnavailable("widget status threw");
            }
          }
        };
        checkAndOpen();
      }
    } catch (err) {
      console.error("Error opening HubSpot widget:", err);
      setIsLoading(false);
      void reportChatUnavailable("opening the widget threw");
    }
  }, [
    bindConversationHandlers,
    detectContentBlocker,
    identifyVisitor,
    reportChatUnavailable,
  ]);

  React.useEffect(() => {
    openHandlerRef.current = openHandler;
  }, [openHandler]);

  const closeHandler = React.useCallback(() => {
    if (!window.HubSpotConversations) return;
    try {
      setChatOpenClass(false);
      window.HubSpotConversations.widget.close();
    } catch (err) {
      console.error("Error closing HubSpot widget:", err);
    }
  }, []);

  return {
    activeConversation,
    unreadCount,
    isLoading,
    openHandler,
    closeHandler,
  };
};
